import {getLixilCampaignInfo} from "../lixilLogic/lixilActions";
import {
  formatCalendarDateToMatchAvailableDate,
  getSelectedLixilDateObject
} from "../appointmentLogic/appointmentActions";
import {replaceMaskToPassPhoneOnTheBackend} from "../userLogic/userActions";

/**
 * returns feature compressed object that can be passed on the backend (without redundant info)
 * @param fromFeature {featureInterface}
 * @returns {object}
 */
const getCompressedFeature = (fromFeature) => {
  if (!fromFeature) {
    return null
  }
  return {
    partName: fromFeature.customDisplayName || fromFeature.displayName,
    sharedFeatureName: fromFeature.sharedFeatureName,
    dbFeatureId: fromFeature.id,
    sku: fromFeature.sku,
    partId: fromFeature.partId,
    groupName: fromFeature.groupName,
    featureDefinitionID: fromFeature.featureDefinitionID,
  }
}

/**
 * is used to compress the data that will be passed in the job data back and force
 * @param allWizardsValues {array}
 * @param allWizardsValues {array}
 */
const getCompressedAllWizardValues = (allWizardsValues) => {
  const allWizardsValuesToSave = allWizardsValues.map(newWizObject => {
    let wizValues = {};
    Object.keys(newWizObject.wizardSelectedValues).forEach(stepIndex => {
      wizValues[stepIndex] = Object.assign({}, newWizObject.wizardSelectedValues[stepIndex])
      if (newWizObject.wizardSelectedValues[stepIndex].wizardGroup) {
        wizValues[stepIndex].wizardGroup = Object.assign(
          {},
          newWizObject.wizardSelectedValues[stepIndex].wizardGroup,
          {featuresExcluded: [], featuresIncluded: []} // to keep clean in json
        )
        if (wizValues[stepIndex].wizardGroup.mergedGroups) {
          wizValues[stepIndex].wizardGroup.mergedGroups = wizValues[stepIndex].wizardGroup.mergedGroups.map(wizGroup => {
            return {id: wizGroup.id, groupName: wizGroup.groupName}
          });
        }
      }
      if (newWizObject.wizardSelectedValues[stepIndex].selectedFeature) {
        wizValues[stepIndex].selectedFeature = getCompressedFeature(
          newWizObject.wizardSelectedValues[stepIndex].selectedFeature,
          newWizObject.quoteIndex
        ); // to keep clean in json
      }
      if (newWizObject.wizardSelectedValues[stepIndex].selectedFeatures) {
        wizValues[stepIndex].selectedFeatures = newWizObject.wizardSelectedValues[stepIndex].selectedFeatures.map(feature => getCompressedFeature(
          feature,
          newWizObject.quoteIndex
        ))
      }
    })
    return Object.assign({}, newWizObject, {wizardSelectedValues: wizValues})
  });
  // console.log("allWizardsValues to save ->", allWizardsValuesToSave);
  return allWizardsValuesToSave
}
const getJobRecords = (store) => {
  const {wizardState, userState} = store;
  return [{
    bvImage: "",
    features: [],
    imageUrl: null,
    installationCost: 0,
    installationTaxes: 0,
    isCreatedInSQFT: false,
    isVisibleInSQFT: false,
    layoutType: 1,
    leadFeatures: [],
    materialCost: 0,
    materialTaxes: 0,
    priceTaxes: [],
    qtCatName: "",
    room: "LeadForm",
    sFtCalcDataJson: null,
    sqFtCalcImage: null,
    staticCanvasImage: "",
    data: JSON.stringify({ // here is the main stored info
      id: 1,
      lookId: 1,
      layout_type: 1,
      cartState: {},
      navigationType: "LeadForm",
      toolVersion: 1,
      userState: Object.assign({}, userState, {
        userInfo: Object.assign({}, userState.userInfo, {
          phone: replaceMaskToPassPhoneOnTheBackend(userState.userInfo.phone)
        })
      }),
      allWizardsValues: getCompressedAllWizardValues(wizardState.allWizardsValues)
    })
  }]
}

export const getSaveJobData = (store, isDelayedSave = false) => {
  const {globalState, userState, appointmentState} = store;

  const {salesRepID, appointmentDate, appointmentTimeSlot, availableAppointmentDates} = appointmentState;

  const lixilCampaignInfo = getLixilCampaignInfo(store);
  if (appointmentDate) {
    const appDate = new Date(appointmentDate);
    lixilCampaignInfo.appointment_date_stc = formatCalendarDateToMatchAvailableDate(appDate, true);
    if (appointmentTimeSlot) {
      const selectedLixilDateObject = getSelectedLixilDateObject(appDate, availableAppointmentDates);
      if (selectedLixilDateObject) {
        const timeSlotObject = selectedLixilDateObject.salesRepsInSlots.find(timeSlotObject => timeSlotObject.timeSlot === appointmentTimeSlot)
        if (timeSlotObject && timeSlotObject.slotStartTime) {
          lixilCampaignInfo.appointment_time_stc = timeSlotObject.slotStartTime[0] === "0"
            ? timeSlotObject.slotStartTime.substring(1) // 04:00 -> 4:00
            : timeSlotObject.slotStartTime;
        }
      }
    }
  }

  const lixilData = {
    salesRepID,
    receiveSmsCheckbox: userState.userInfo.receiveSmsCheckbox,
    ...lixilCampaignInfo,
    isDelayedSave,
  }

  const appForm = document.getElementById("app-form");
  if (appForm && appForm.children) {
    [].slice.call(appForm.children).forEach(input => {
      if (input.name && input.value) {
        lixilData[input.name] = input.value;
      }
    })
  }

  const saveDataBeforeConverting = {
    // fast
    jobId: document.kvConfig.jobId || globalState.jobId,
    fromBV: true,
    fabricatorId: document.kvConfig.userId,
    notes: [],
    documents: [],
    employeeId: 0,
    shareUrl: "",
    discountSections: [],
    installations: [],
    subId: document.subId && document.subId.toString ? document.subId.toString() : null,
    total: {
      materialCost: 0,
      installationCost: 0,
      installationTaxes: 0,
      materialTaxes: 0,
      priceTaxes: [],
      totalWithTaxes: 0,
    },
    records: getJobRecords(store),
    // fast end
    yesNoConsultationRequest: false,
    submit: true,
    fromCheckOutPage: false,
    sendConsumerEmail: true,
    customerEmail: userState.userInfo.email || userState.registeredUser.email || "",
    salesPerson: "",
    salesPersonEmail: "",
    leadOriginatorId: (window.salesPerson ? window.salesPerson.rawVal / ( Math.floor(Math.PI * 1000)) : 0),
    postalCode: userState.userInfo.postal,
    // isNewUser: !(code below) || (userState.registeredUser.email !== userState.userInfo.email), // not sure if we need it
    isNewUser: !(document.kvConfig.jobId || globalState.jobId),
    state: "quote",
    fromTool: "LF", //"BV",
    toolVersion: document.kvConfig.toolVersion,
    visualizerType: "LeadForm",
    navigationType: "LeadForm",
    quoteRequestType: "ScheduleAnAppointment", // QuoteRequestType
    currency: "USD",
    installationTaxesRate: 0,
    materialTaxesRate: 0,
    fromDashboard: document.kvConfig.isBackend,
    callFabricator: document.kvConfig.callFabricatorOnSave || false,
    language: "en-US",
    salesRepID,
    lixilData,
  }
  console.log("saveData", saveDataBeforeConverting);
  const formData = new FormData();
  formData.append('json', JSON.stringify(saveDataBeforeConverting));
  return formData
}
